import React, { useEffect, useState } from 'react';
import { CalendarDays, CheckCircle2, FolderOpen, Loader2 } from 'lucide-react';
import { apiService } from '../../services/api';
import MailAccessJournal from './MailAccessJournal';

// Connexion Google du compte : Drive (fichiers des projets) et agenda (réunions ; créneaux bloqués par Nexus depuis le
// 2026-10-09). Une seule connexion Google ; l'agenda s'y ajoute avec un consentement supplémentaire, et l'écriture
// (bloquer un créneau) avec un second pour qui l'avait relié en lecture seule.
const Row = ({ icon: Icon, title, text, connected, action }) => (
  <div className="flex items-start gap-3 rounded-xl border border-slate-100 p-3">
    <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${connected ? 'text-emerald-600' : 'text-slate-400'}`} />
    <div className="min-w-0 flex-1">
      <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
        {title}
        {connected && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
      </p>
      <p className="text-xs text-slate-500">{text}</p>
    </div>
    {action}
  </div>
);

const GoogleConnections = () => {
  const [drive, setDrive] = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const load = () => Promise.all([
    apiService.getGoogleDriveStatus().then(setDrive).catch(() => setDrive({ connected: false })),
    apiService.getGoogleCalendarStatus().then(setCalendar).catch(() => setCalendar({ connected: false })),
  ]);

  useEffect(() => { load(); }, []);

  const connect = async (kind) => {
    setBusy(kind);
    setError(null);
    try {
      const returnTo = window.location.pathname;
      const { authorization_url: url } = kind === 'calendar'
        ? await apiService.startGoogleCalendarAuth(returnTo)
        : await apiService.startGoogleDriveAuth(returnTo);
      window.location.href = url;
    } catch (err) {
      setError(err.message);
      setBusy(null);
    }
  };

  const disconnect = async () => {
    setBusy('disconnect');
    try {
      await apiService.disconnectGoogleDrive();
      await load();
    } finally {
      setBusy(null);
    }
  };

  const button = (kind, label) => (
    <button type="button" onClick={() => connect(kind)} disabled={Boolean(busy)}
      className="shrink-0 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-700 disabled:opacity-50">
      {busy === kind ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : label}
    </button>
  );

  if (!drive || !calendar) {
    return <p className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Chargement…</p>;
  }

  return (
    <div className="space-y-2">
      <Row icon={FolderOpen} title="Google Drive" connected={drive.connected}
        text="Parcourir et lire les dossiers Drive des projets, avec tes droits Google."
        action={!drive.connected && button('drive', 'Connecter')} />
      <Row icon={CalendarDays} title="Agenda Google" connected={calendar.connected}
        text={calendar.can_write
          ? 'Tes réunions (titre, participants et projet préremplis quand tu enregistres) ; Nexus peut aussi bloquer des créneaux quand tu le lui demandes.'
          : calendar.connected
            ? 'Lecture seule : Nexus voit tes réunions mais ne peut pas bloquer de créneau pour toi.'
            : 'Tes réunions (titre, participants et projet préremplis) ; Nexus peut bloquer des créneaux quand tu le lui demandes.'}
        action={!calendar.connected ? button('calendar', 'Lier mon agenda')
          : !calendar.can_write && button('calendar', 'Autoriser Nexus à bloquer des créneaux')} />
      {(drive.connected || calendar.connected) && (
        <button type="button" onClick={disconnect} disabled={Boolean(busy)}
          className="text-xs text-slate-400 hover:text-rose-600">Déconnecter mon compte Google (Drive et agenda)</button>
      )}
      {error && <p className="text-xs text-rose-600">{error}</p>}
      <MailAccessJournal />
    </div>
  );
};

export default GoogleConnections;
